import { FC, ReactNode } from 'react';
import type {
  TreeSelectRowControls,
  TreeSelectVirtualizationState,
} from '@/storybook/components/FormCollection/TreeSelect/hooks/useTreeSelectVirtualization';
import type { VisibleTreeRow } from '@/storybook/components/FormCollection/TreeSelect/hooks/useTreeSelectRendering';
import styles from './TreeSelectRows.module.css';

interface TreeSelectRowsProps {
  visibleRows: readonly VisibleTreeRow[];
  renderTreeRow: (visibleRow: VisibleTreeRow, tabIndex?: number) => ReactNode;
  virtualization: TreeSelectVirtualizationState & TreeSelectRowControls;
}

function getRowTabIndex(rowIndex: number, tabbableIndex: number): number {
  return rowIndex === tabbableIndex ? 0 : -1;
}

const TreeSelectRows: FC<TreeSelectRowsProps> = ({ visibleRows, renderTreeRow, virtualization }) => {
  const {
    shouldVirtualize,
    virtualItems,
    tabbableIndex,
    handleVirtualListKeyDown,
    handleVirtualRowFocus,
    registerVirtualRow,
  } = virtualization;

  return (
    <div
      className={styles.virtualContainer}
      style={shouldVirtualize ? { height: virtualization.totalSize } : undefined}
      data-testid={shouldVirtualize ? 'virtualizedTreeRows' : undefined}
      onKeyDown={handleVirtualListKeyDown}
    >
      {shouldVirtualize
        ? virtualItems.map(virtualItem => {
            const visibleRow = visibleRows[virtualItem.index];
            if (!visibleRow) return null;
            return (
              <div
                key={visibleRow.properties.path}
                ref={node => registerVirtualRow(virtualItem.index, node)}
                className={styles.virtualRow}
                data-index={virtualItem.index}
                data-tree-row-index={virtualItem.index}
                onFocus={() => handleVirtualRowFocus(virtualItem.index)}
                style={{ transform: `translateY(${virtualItem.start}px)` }}
              >
                {renderTreeRow(visibleRow, getRowTabIndex(virtualItem.index, tabbableIndex))}
              </div>
            );
          })
        : visibleRows.map((visibleRow, visibleRowIndex) => (
            <div
              key={visibleRow.properties.path}
              data-tree-row-index={visibleRowIndex}
              onFocus={() => handleVirtualRowFocus(visibleRowIndex)}
            >
              {renderTreeRow(visibleRow, getRowTabIndex(visibleRowIndex, tabbableIndex))}
            </div>
          ))}
    </div>
  );
};

export default TreeSelectRows;
